import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight, CalendarDays, X } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/utils";
import { formatDate as formatLocaleDate, formatNumber } from "@/lib/formatters";
import { getStoredLanguage, translate, useI18n } from "@/i18n";
import {
  addMonths,
  clampMonth,
  monthAllowed,
  navBounds,
  yearAllowed,
  yearPage,
} from "@/lib/calendarGrid";

// Compact date picker. Design spec: docs/design/COMPACT_DATE_PICKER.md

const toLocalDate = (value) => {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toDateValue = (date) =>
  date
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : "";

// The picker also renders outside the i18n provider (e.g. isolated tests).
function useCalendarLanguage() {
  try {
    return useI18n().language;
  } catch {
    return getStoredLanguage();
  }
}

const NO_DAY = { day: undefined, year: undefined };
const monthName = (year, month, language, width = "long") =>
  formatLocaleDate(new Date(year, month, 1), language, { ...NO_DAY, month: width });
const yearName = (year, language) => formatNumber(year, language, { useGrouping: false });

// Theme tokens only: the shadcn `accent` utility does not match this app's
// --accent variable, which made "today" render white on white.
const cellBase =
  "inline-flex items-center justify-center rounded-md text-[13px] tabular-nums text-[var(--text-primary)] transition-colors " +
  "hover:bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] hover:text-[var(--accent)] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] " +
  "disabled:pointer-events-none disabled:opacity-35";
const cellSelected = "!bg-[var(--accent)] !text-white font-semibold hover:!bg-[var(--accent-hover)]";
const cellToday = "font-semibold text-[var(--accent)] ring-1 ring-inset ring-[var(--accent)]";

function HeaderButton({ className, children, ...props }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-7 items-center gap-0.5 rounded-md px-1.5 text-[13px] font-semibold text-[var(--text-primary)] transition-colors",
        "hover:bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] hover:text-[var(--accent)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]",
        "disabled:pointer-events-none disabled:opacity-30",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function Calendar({
  className,
  classNames,
  selected,
  onSelect,
  defaultMonth,
  fromYear,
  toYear,
  min,
  max,
  disabled,
  showOutsideDays = true,
  ...props
}) {
  const language = useCalendarLanguage();
  const t = (key) => translate(language, key);
  const today = new Date();
  const bounds = React.useMemo(
    () => navBounds({ fromYear, toYear, minDate: min, maxDate: max }),
    [fromYear, toYear, min, max],
  );
  const [month, setMonth] = React.useState(() => clampMonth(defaultMonth || selected || today, bounds));
  const [view, setView] = React.useState("days");
  const [yearFocus, setYearFocus] = React.useState(month.getFullYear());
  const year = month.getFullYear();
  const page = yearPage(yearFocus);

  const nav = {
    days: {
      prev: () => setMonth(addMonths(month, -1)),
      next: () => setMonth(addMonths(month, 1)),
      canPrev: monthAllowed(addMonths(month, -1).getFullYear(), addMonths(month, -1).getMonth(), bounds),
      canNext: monthAllowed(addMonths(month, 1).getFullYear(), addMonths(month, 1).getMonth(), bounds),
      labels: [t("calendar.previousMonth"), t("calendar.nextMonth")],
    },
    months: {
      prev: () => setMonth(clampMonth(new Date(year - 1, month.getMonth(), 1), bounds)),
      next: () => setMonth(clampMonth(new Date(year + 1, month.getMonth(), 1), bounds)),
      canPrev: yearAllowed(year - 1, bounds),
      canNext: yearAllowed(year + 1, bounds),
      labels: [t("calendar.previousYear"), t("calendar.nextYear")],
    },
    years: {
      prev: () => setYearFocus(yearFocus - 12),
      next: () => setYearFocus(yearFocus + 12),
      canPrev: yearAllowed(page[0] - 1, bounds),
      canNext: yearAllowed(page[11] + 1, bounds),
      labels: [t("calendar.previousYears"), t("calendar.nextYears")],
    },
  }[view];

  const openYears = () => {
    setYearFocus(year);
    setView("years");
  };

  return (
    <div className={cn("w-[15.5rem] select-none p-2.5", className)} data-testid="calendar">
      <div className="mb-1.5 flex items-center justify-between">
        <HeaderButton onClick={nav.prev} disabled={!nav.canPrev} aria-label={nav.labels[0]} className="w-7 justify-center px-0">
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
        </HeaderButton>
        <div className="flex items-center" aria-live="polite">
          {view === "days" && (
            <HeaderButton onClick={() => setView("months")} aria-label={t("calendar.chooseMonth")} data-testid="calendar-month-label">
              {monthName(year, month.getMonth(), language)}
            </HeaderButton>
          )}
          {view !== "years" ? (
            <HeaderButton onClick={openYears} aria-label={t("calendar.chooseYear")} data-testid="calendar-year-label">
              {yearName(year, language)}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </HeaderButton>
          ) : (
            <span className="px-1.5 text-[13px] font-semibold tabular-nums text-[var(--text-primary)]" data-testid="calendar-year-range">
              {yearName(page[0], language)} – {yearName(page[11], language)}
            </span>
          )}
        </div>
        <HeaderButton onClick={nav.next} disabled={!nav.canNext} aria-label={nav.labels[1]} className="w-7 justify-center px-0">
          <ChevronRight className="h-4 w-4 rtl:rotate-180" />
        </HeaderButton>
      </div>

      {view === "days" && (
        <DayPicker
          mode="single"
          selected={selected}
          onSelect={onSelect}
          month={month}
          onMonthChange={setMonth}
          fromMonth={bounds.first || undefined}
          toMonth={bounds.last || undefined}
          disabled={disabled}
          showOutsideDays={showOutsideDays}
          fixedWeeks
          dir={language === "ar" ? "rtl" : "ltr"}
          components={{ Caption: () => null }}
          formatters={{
            formatDay: (date) => formatNumber(date.getDate(), language),
            formatWeekdayName: (date) =>
              formatLocaleDate(date, language, { ...NO_DAY, month: undefined, weekday: language === "ar" ? "narrow" : "short" }),
          }}
          classNames={{
            months: "",
            month: "",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell: "w-8 pb-1 text-[10.5px] font-medium uppercase tracking-wide text-[var(--text-muted)]",
            row: "mt-0.5 flex w-full",
            cell: "relative h-8 w-8 p-0 text-center",
            day: cn(cellBase, "h-8 w-8"),
            day_selected: cellSelected,
            day_today: cellToday,
            day_outside: "!text-[var(--text-muted)] opacity-50",
            day_disabled: "!text-[var(--text-muted)] opacity-35 hover:bg-transparent",
            day_hidden: "invisible",
            vhidden: "sr-only",
            ...classNames,
          }}
          {...props}
        />
      )}

      {view === "months" && (
        <div className="grid grid-cols-3 gap-1 pt-1" data-testid="calendar-months">
          {Array.from({ length: 12 }, (_, m) => {
            const isSelected = selected && selected.getFullYear() === year && selected.getMonth() === m;
            const isCurrent = today.getFullYear() === year && today.getMonth() === m;
            return (
              <button
                key={m}
                type="button"
                disabled={!monthAllowed(year, m, bounds)}
                onClick={() => {
                  setMonth(new Date(year, m, 1));
                  setView("days");
                }}
                className={cn(cellBase, "h-[3.25rem]", isSelected ? cellSelected : isCurrent && cellToday)}
                data-testid={`calendar-month-${m + 1}`}
              >
                {monthName(year, m, language, "short")}
              </button>
            );
          })}
        </div>
      )}

      {view === "years" && (
        <div className="grid grid-cols-3 gap-1 pt-1" data-testid="calendar-years">
          {page.map((y) => (
            <button
              key={y}
              type="button"
              disabled={!yearAllowed(y, bounds)}
              onClick={() => {
                setMonth(clampMonth(new Date(y, month.getMonth(), 1), bounds));
                setView("months");
              }}
              className={cn(cellBase, "h-[3.25rem]", y === year ? cellSelected : y === today.getFullYear() && cellToday)}
              data-testid={`calendar-year-${y}`}
            >
              {yearName(y, language)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
Calendar.displayName = "Calendar";

function DatePicker({
  value,
  onChange,
  min,
  max,
  fromYear,
  toYear,
  placeholder = "Select date",
  ariaLabel,
  testId,
  className,
  defaultMonth,
  clearable = false,
  clearLabel = "Clear date",
}) {
  const [open, setOpen] = React.useState(false);
  const currentYear = new Date().getFullYear();
  const selectedDate = toLocalDate(value);
  const minDate = toLocalDate(min);
  const maxDate = toLocalDate(max);
  const disabled = [minDate && { before: minDate }, maxDate && { after: maxDate }].filter(Boolean);
  // Open on the chosen date; otherwise on the caller's month (YYYY-MM or a date).
  const openMonth = selectedDate || toLocalDate(defaultMonth ? `${defaultMonth.slice(0, 7)}-01` : "");
  const showClear = clearable && Boolean(value);

  const picker = (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          data-testid={testId}
          className={cn(
            "group mt-1 h-10 w-full justify-start text-left font-normal",
            value ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]",
            showClear && "pe-9",
            className,
          )}
        >
          <CalendarDays className="mr-2 h-4 w-4 text-[var(--accent)] group-hover:text-white" />
          {selectedDate ? formatDate(value) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-auto max-w-[calc(100vw-2rem)] p-0"
        align="start"
        side="bottom"
        sideOffset={6}
        avoidCollisions={false}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Calendar
          selected={selectedDate}
          defaultMonth={openMonth}
          onSelect={(date) => {
            // Clicking the selected day again keeps it; the picker just closes.
            if (date) onChange(toDateValue(date));
            setOpen(false);
          }}
          fromYear={fromYear ?? currentYear - 100}
          toYear={toYear ?? currentYear + 10}
          min={minDate}
          max={maxDate}
          disabled={disabled.length ? disabled : undefined}
        />
      </PopoverContent>
    </Popover>
  );
  if (!clearable) return picker;

  // Only clearable pickers get a wrapper, so existing layouts are unchanged.
  return (
    <div className="relative">
      {picker}
      {showClear && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label={clearLabel}
          title={clearLabel}
          data-testid={testId ? `${testId}-clear` : undefined}
          className="absolute end-1 bottom-1 flex h-8 w-8 items-center justify-center rounded text-[var(--text-muted)] hover:text-[var(--text-primary)]"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export { Calendar, DatePicker };
